<template>
    <div class="report-badges" :class="{'report-badges--compact': compact}">
        <b-badge
            v-for="badge in badges"
            :key="badge.key"
            :variant="badge.variant"
            class="report-badge"
            v-b-tooltip.hover.html
            :title="badge.title"
        >
            <i v-if="badge.icon" :class="badge.icon"></i>
            {{ badge.text }}
        </b-badge>
        <span v-if="!badges.length && showEmpty" class="text-muted">Без замечаний</span>
    </div>
</template>

<script>
export default {
    name: 'ReportBadges',
    props: {
        report: {
            type: Object,
            required: true
        },
        compact: {
            type: Boolean,
            default: false
        },
        showEmpty: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        badges() {
            const report = this.report || {};
            const flags = report.flags || {};
            const stats = report.stats || {};
            const result = [];

            if (report.is_annulled || (report.annulment && report.annulment.is_annulled)) {
                const annulment = report.annulment || {};
                const author = this.authorName(annulment.request_author, 'Автор заявки не найден');
                const reason = annulment.reason || 'Причина не указана';
                result.push({
                    key: 'annulled',
                    text: 'АННУЛИРОВАН',
                    variant: 'danger',
                    icon: 'fas fa-ban',
                    title: `<strong>НЕ УЧИТЫВАТЬ</strong><br>Причина: ${this.escapeHtml(reason)}<br>Заявку создал: ${this.escapeHtml(author)}`
                });
            }

            if (flags.late) {
                result.push({
                    key: 'late',
                    text: 'Просрочка',
                    variant: 'danger',
                    icon: 'fas fa-clock',
                    title: report.reason_for_delay
                        ? this.explanationTooltip(report.reason_for_delay, report.author)
                        : (
                        (report.submission && report.submission.delay && report.submission.delay.text) ||
                        'Отчёт подан с задержкой'
                    )
                });
            }

            if (flags.no_photo) {
                result.push({
                    key: 'no_photo',
                    text: 'Фото не поданы',
                    variant: flags.no_photo_reason ? 'warning' : 'danger',
                    icon: 'fas fa-camera',
                    title: report.empty_photo_comment
                        ? this.explanationTooltip(report.empty_photo_comment, report.author)
                        : 'Фото по отчёту не найдены'
                });
            }

            if (flags.system_photo_issue) {
                const parts = [];
                if (stats.telegram_failed_media_count) {
                    parts.push(`сбой доставки: ${stats.telegram_failed_media_count}`);
                }
                if (stats.processed_media_count) {
                    parts.push(`ожидают системной отправки: ${stats.processed_media_count}`);
                }
                if (stats.shift_not_found_media_count) {
                    parts.push(`ошибка привязки смены: ${stats.shift_not_found_media_count}`);
                }
                if (stats.source_missing_media_count) {
                    parts.push(`локальный исходник не найден: ${stats.source_missing_media_count}`);
                }
                result.push({
                    key: 'system_photo_issue',
                    text: 'Сбой доставки фото',
                    variant: 'danger',
                    icon: 'fas fa-exclamation-triangle',
                    title: parts.length ? parts.join('; ') : 'Фото есть, но система не завершила доставку'
                });
            }

            if (flags.processing_photo) {
                const waitsForStorage = Number(stats.temp_media_count) > 0;
                result.push({
                    key: 'processing_photo',
                    text: waitsForStorage ? 'Фото ожидают хранилище' : 'Фото обрабатываются',
                    variant: waitsForStorage ? 'warning' : 'info',
                    icon: 'fas fa-sync-alt',
                    title: waitsForStorage
                        ? 'Оригиналы сохранены локально. Система автоматически продолжит перенос после восстановления файлового хранилища объекта.'
                        : 'Фото приняты системой и ещё проходят обработку'
                });
            }

            if (flags.no_fuel) {
                result.push({
                    key: 'no_fuel',
                    text: 'Без топлива',
                    variant: 'secondary',
                    icon: 'fas fa-gas-pump',
                    title: 'В отчёте нет заправки или все значения равны нулю'
                });
            }

            if (flags.no_telegram_message) {
                result.push({
                    key: 'no_telegram',
                    text: 'Нет Telegram ID',
                    variant: 'dark',
                    icon: 'fab fa-telegram-plane',
                    title: 'У смены не зафиксирован ID Telegram-сообщения отчёта'
                });
            }

            return result;
        }
    },
    methods: {
        escapeHtml(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
        authorName(user, fallback = 'автор отчёта') {
            if (!user) {
                return fallback;
            }
            if (user.full_name || user.fullName) {
                return user.full_name || user.fullName;
            }
            return [
                user.last_name || user.lastName || '',
                user.first_name || user.firstName || ''
            ].join(' ').trim() || fallback;
        },
        explanationTooltip(reason, author) {
            return [
                '<div class="report-badge-tooltip">',
                `<blockquote>${this.escapeHtml(reason)}</blockquote>`,
                `<div class="report-badge-tooltip__footer">- заверяет ${this.escapeHtml(this.authorName(author))}</div>`,
                '</div>'
            ].join('');
        }
    }
}
</script>

<style scoped>
.report-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.report-badge {
    align-items: center;
    display: inline-flex;
    gap: 4px;
    line-height: 1.25;
    white-space: normal;
}

.report-badges--compact .report-badge {
    font-size: 11px;
    padding: .25rem .4rem;
}

@media (max-width: 767.98px) {
    .report-badges {
        gap: 3px;
    }

    .report-badge {
        min-height: 22px;
        max-width: 100%;
    }

    .report-badges--compact .report-badge {
        font-size: 10px;
        padding: .2rem .35rem;
    }
}
</style>

<style>
.tooltip .report-badge-tooltip {
    max-width: 280px;
    text-align: left;
}

.tooltip .report-badge-tooltip blockquote {
    border-left: 3px solid rgba(255, 255, 255, .65);
    font-style: italic;
    margin: 0;
    padding: 2px 0 2px 8px;
    white-space: pre-wrap;
}

.tooltip .report-badge-tooltip__footer {
    font-size: 12px;
    font-weight: 700;
    margin-top: 6px;
}
</style>
